import { Chapter } from "./Chapter";
import { Reveal } from "./Reveal";
import { PawWatermark } from "./Paws";
import { CatCrest } from "./CatCrest";
import { photos, site } from "../data/siteConfig";
import { Mountain, Bird, Sun } from "lucide-react";

const MILESTONES = [
    {
        mark: "Where it began",
        text: "Mākara Village Boarding Cattery has been caring for cats for more than twenty years. Linda Stephens established the cattery and built the care, routines and reputation it's known for today.",
    },
    {
        mark: "A family effort",
        text: "It has always been a family business. Over the years, family members — including Linda's son Cody — have pitched in through holidays, homecomings and busy summer seasons.",
    },
    {
        mark: "Today",
        text: "Cody now runs the cattery alongside his partner Tran, continuing the same hands-on family care. Regulars are greeted like old friends, and new faces are warmly welcomed.",
    },
];

export const Story = () => (
    <section
        className="relative overflow-hidden bg-coal py-24 text-cream sm:py-32"
        data-testid="story-section"
    >
        <PawWatermark tone="dark" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
            <Chapter
                no="01"
                id="story"
                dark
                eyebrow="Our Story"
                title={
                    <>
                        A family-run cattery, <em>over 20 years</em> in the
                        making.
                    </>
                }
            />

            <div className="mt-10 max-w-3xl space-y-5" data-testid="story-welcome">
                {site.storyWelcome.map((p, i) => (
                    <Reveal key={p.slice(0, 32)} delay={0.06 * i}>
                        <p
                            className={
                                i === 0
                                    ? "font-serif text-xl italic leading-relaxed text-cream sm:text-2xl"
                                    : "leading-relaxed text-stone-300"
                            }
                            data-testid={`story-welcome-${i}`}
                        >
                            {p}
                        </p>
                    </Reveal>
                ))}
            </div>

            <div className="mt-14 grid gap-12 lg:grid-cols-12">
                <div className="max-w-3xl lg:col-span-7">
                    <Reveal>
                        <blockquote
                            className="border-l-2 border-stone-500 pl-6 font-serif text-2xl italic leading-snug text-cream sm:text-3xl"
                            data-testid="story-quote"
                        >
                            “More than 20 years of family care in Mākara.”
                        </blockquote>
                    </Reveal>

                    <div className="mt-12 space-y-10">
                        {MILESTONES.map((m, i) => (
                            <Reveal key={m.mark} delay={0.08 * i}>
                                <div className="flex gap-6" data-testid={`story-milestone-${i}`}>
                                    <span className="mt-1 shrink-0 font-mono text-[11px] uppercase tracking-[0.25em] text-stone-500">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <div>
                                        <h3 className="font-serif text-xl font-semibold text-cream sm:text-2xl">
                                            {m.mark}
                                        </h3>
                                        <p className="mt-2 leading-relaxed text-stone-300">
                                            {m.text}
                                        </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.2}>
                        <p className="mt-10 leading-relaxed text-stone-300">
                            When you book with Mākara, you're leaving your cat
                            with a real family, in a real place we love — and
                            we think the cats can tell.
                        </p>
                    </Reveal>
                </div>

                {/* Heritage identity */}
                <div className="lg:col-span-5">
                    <Reveal delay={0.1}>
                        <div
                            className="rounded-3xl border border-[#7C8966]/25 bg-[#2A2D24] p-8 sm:p-10"
                            data-testid="story-heritage-card"
                        >
                            <CatCrest
                                className="h-24 w-24 text-cream"
                                showText
                            />
                            <h3 className="mt-6 font-serif text-2xl font-semibold text-cream">
                                A name with history
                            </h3>
                            <p className="mt-3 text-sm leading-relaxed text-stone-300 sm:text-base">
                                Our identity began with Linda's original Mākara
                                Cattery business cards — the arched name, the
                                little line-art cat and the paw print. This
                                website is their grandchild: the same family
                                business, gently modernised.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>

            {/* Wide authentic property photograph (when supplied) */}
            {photos.propertyWide && (
                <Reveal delay={0.1}>
                    <figure
                        className="spotlight-frame mt-16 overflow-hidden rounded-3xl border border-[#7C8966]/25"
                        data-testid="story-property-photo"
                    >
                        <img
                            src={photos.propertyWide.src}
                            alt={photos.propertyWide.alt}
                            loading="lazy"
                            className="aspect-[8/3] w-full object-cover"
                        />
                    </figure>
                </Reveal>
            )}

            {/* The Mākara environment */}
            <Reveal delay={0.1}>
                <div
                    className="mt-16 rounded-3xl border border-[#7C8966]/25 bg-[#2A2D24] p-8 sm:p-12"
                    data-testid="story-environment"
                >
                    <div className="grid items-center gap-8 md:grid-cols-2">
                        <div>
                            <p className="font-mono text-xs uppercase tracking-[0.3em] text-stone-400">
                                The Mākara experience
                            </p>
                            <h3 className="mt-4 font-serif text-3xl font-medium leading-tight text-cream sm:text-4xl">
                                Peaceful Mākara — surprisingly close to the
                                city.
                            </h3>
                            <p className="mt-4 leading-relaxed text-stone-300">
                                Tucked at the rear of the family property, the
                                cattery looks out over open space, trees and
                                hills — birdsong, sunshine and fresh country
                                air, well away from city noise. Only a short
                                drive over the hill from Karori.
                            </p>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            {[
                                { icon: Mountain, label: "Surrounding hills" },
                                { icon: Bird, label: "Birdlife & fresh air" },
                                { icon: Sun, label: "Sunshine & open space" },
                            ].map(({ icon: Icon, label }) => (
                                <div
                                    key={label}
                                    className="flex flex-col items-center gap-3 rounded-2xl bg-white/5 p-5 text-center transition-transform duration-300 hover:-translate-y-1"
                                >
                                    <Icon className="h-6 w-6 text-euca" />
                                    <span className="text-xs font-medium leading-snug text-stone-400">
                                        {label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);
